import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Pagination } from '../../components/ui/pagination';
import {
  Trophy,
  Award,
  Star,
  Medal,
  Search,
  BookOpen,
  Clock,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Target,
} from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
} from 'date-fns';
import { ACTIVITY_TYPE } from '../../constants/activity-types';
import {
  useUserStats,
  useUserActivity,
  useUserCourses,
  useUserTests,
  ActivityLogEntry,
  usePersonDetail,
} from '../../hooks/use-user-stats';
import { useWorkspace } from '../../context/workspace-context';
import { TestStatusBadge } from '../../components/test-status-badge';
import { USER_STATS_TAB } from '../../constants/user-stats-tab';
import { getUserStatsTabFromUrl } from '../../helpers/get-user-stats-tab-from-url';
import { formatTestDuration } from '../../helpers/format-test-duration';
import { formatTestDate } from '../../helpers/format-test-date';

const PAGE_SIZE = 10;

export default function StatsPage() {
  const { id } = useParams();
  const userId = id ? parseInt(id) : undefined;
  const { currentWorkspace } = useWorkspace();

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() =>
    getUserStatsTabFromUrl(searchParams),
  );
  const [testsPage, setTestsPage] = useState(1);
  const [expandedCourseId, setExpandedCourseId] = useState<number | null>(null);

  const { data: stats, isLoading: statsLoading } = useUserStats(
    userId,
    currentWorkspace?.id,
  );

  const { data: activity } = useUserActivity(
    userId,
    currentWorkspace?.id,
    activityPage,
    PAGE_SIZE,
    activeTab === USER_STATS_TAB.ACTIVITY,
  );

  const { data: courses } = useUserCourses(
    userId,
    currentWorkspace?.id,
    coursesPage,
    PAGE_SIZE,
    searchTerm,
    activeTab === USER_STATS_TAB.COURSES,
  );

  const { data: personDetail, isLoading: personDetailLoading } =
    usePersonDetail(currentWorkspace?.id, userId, !!expandedCourseId);

  const { data: userTests } = useUserTests(
    userId,
    currentWorkspace?.id,
    testsPage,
    PAGE_SIZE,
    activeTab === USER_STATS_TAB.TESTS,
  );

  const formatDate = (date: string) => {
    const d = new Date(date);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    if (isThisWeek(d)) return format(d, 'EEEE');
    if (isThisMonth(d)) return format(d, 'MMMM d');
    return format(d, 'MMMM d, yyyy');
  };

  const groupActivitiesByDate = (activities: ActivityLogEntry[]) => {
    const groups: { [key: string]: ActivityLogEntry[] } = {};
    activities.forEach((activity) => {
      const date = formatDate(activity.createdAt);
      if (!groups[date]) groups[date] = [];
      groups[date].push(activity);
    });
    return groups;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case ACTIVITY_TYPE.PERFECT_LEVEL:
        return <Star className="h-5 w-5 text-yellow-500" />;
      case ACTIVITY_TYPE.LEVEL_COMPLETED:
        return <Trophy className="h-5 w-5 text-green-500" />;
      case ACTIVITY_TYPE.LEVEL_STARTED:
        return <Medal className="h-5 w-5 text-blue-500" />;
      case ACTIVITY_TYPE.COURSE_STARTED:
        return <BookOpen className="h-5 w-5 text-emerald-500" />;
      case ACTIVITY_TYPE.TEST_STARTED:
        return <Clock className="h-5 w-5 text-indigo-500" />;
      case ACTIVITY_TYPE.TEST_COMPLETED:
        return <Trophy className="h-5 w-5 text-purple-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActivityMessage = (activity: ActivityLogEntry): string => {
    switch (activity.type) {
      case ACTIVITY_TYPE.LEVEL_STARTED:
        return `Started level: ${activity.levelName}`;
      case ACTIVITY_TYPE.LEVEL_COMPLETED:
        return `Completed level: ${activity.levelName}`;
      case ACTIVITY_TYPE.PERFECT_LEVEL:
        return `Perfect score on level: ${activity.levelName}`;
      case ACTIVITY_TYPE.COURSE_STARTED:
        return `Started course: ${activity.courseName}`;
      case ACTIVITY_TYPE.TEST_STARTED:
        return 'Started a test';
      case ACTIVITY_TYPE.TEST_COMPLETED:
        return 'Completed a test';
      default:
        return activity.type;
    }
  };

  if (statsLoading) return <LoadingSpinner />;
  if (!stats) return <div>Error loading stats. Please try again.</div>;

  const activityList =
    activeTab === USER_STATS_TAB.ACTIVITY && activity
      ? activity.activities
      : stats.recentActivity;
  const groupedActivities = groupActivitiesByDate(activityList);

  const coursesList =
    activeTab === USER_STATS_TAB.COURSES && courses
      ? courses.courses
      : stats.courseProgress;

  return (
    <div className="space-y-8 p-6">
      {/* User Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">
              {stats.user.name?.charAt(0)}
              {stats.user.surname?.charAt(0)}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {stats.user.name} {stats.user.surname}
            </h1>
            <p className="text-sm text-muted-foreground">User Statistics</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-accent-orange relative overflow-hidden rounded-3xl">
          <CardContent className="p-6">
            <div className="flex flex-col text-title">
              <span className="text-sm opacity-75">Total Points</span>
              <div className="flex items-center space-x-2">
                <Trophy className="h-5 w-5" />
                <span className="text-2xl font-bold">{stats.totalPoints}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-accent-purple relative overflow-hidden rounded-3xl">
          <CardContent className="p-6">
            <div className="flex flex-col text-title">
              <span className="text-sm opacity-75">Longest Streak</span>
              <div className="flex items-center space-x-2">
                <Medal className="h-5 w-5" />
                <span className="text-2xl font-bold">
                  {stats.longestStreak}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-accent-pink relative overflow-hidden rounded-3xl">
          <CardContent className="p-6">
            <div className="flex flex-col text-title">
              <span className="text-sm opacity-75">Perfect Levels</span>
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5" />
                <span className="text-2xl font-bold">
                  {stats.perfectLevelsCount}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-accent-blue relative overflow-hidden rounded-3xl">
          <CardContent className="p-6">
            <div className="flex flex-col text-title">
              <span className="text-sm opacity-75">Badges Earned</span>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5" />
                <span className="text-2xl font-bold">
                  {stats.badgesEarnedCount}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-100 relative overflow-hidden rounded-3xl">
          <CardContent className="p-6">
            <div className="flex flex-col text-title">
              <span className="text-sm opacity-75">Avg Test Score</span>
              <div className="flex items-center space-x-2">
                <Target className="h-5 w-5" />
                <span className="text-2xl font-bold">
                  {stats.averageTestScore !== null
                    ? `${stats.averageTestScore}%`
                    : '—'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        defaultValue={activeTab}
        className="space-y-4"
        onValueChange={setActiveTab}
      >
        <TabsList>
          <TabsTrigger value={USER_STATS_TAB.OVERVIEW}>Overview</TabsTrigger>
          <TabsTrigger value={USER_STATS_TAB.ACTIVITY}>Activity</TabsTrigger>
          <TabsTrigger value={USER_STATS_TAB.COURSES}>Courses</TabsTrigger>
          <TabsTrigger value={USER_STATS_TAB.TESTS}>Tests</TabsTrigger>
        </TabsList>

        <TabsContent value={USER_STATS_TAB.OVERVIEW} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Recent Activity Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Latest user actions
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.recentActivity.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-accent/5"
                    >
                      {getActivityIcon(log.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {getActivityMessage(log)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(
                            new Date(log.createdAt),
                            "dd MMM yyyy 'at' HH:mm",
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Course Progress Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Course Progress</CardTitle>
                <p className="text-sm text-muted-foreground">Active courses</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.courseProgress.map((course) => {
                    const progress =
                      course.totalLevels > 0
                        ? (course.completedLevels / course.totalLevels) * 100
                        : 0;
                    return (
                      <div
                        key={course.courseId}
                        className="p-4 rounded-lg bg-accent/5"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium truncate">
                            {course.courseName}
                            {course.isDeleted && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (Archived)
                              </span>
                            )}
                          </p>
                          <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              {course.totalPoints} pts
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Progress</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-accent/20">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {course.completedLevels} of {course.totalLevels}{' '}
                          levels completed
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value={USER_STATS_TAB.ACTIVITY} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">All user activity</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {Object.entries(groupedActivities).map(([date, activities]) => (
                  <div key={date} className="relative">
                    <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 py-2">
                      <h3 className="text-sm font-medium text-muted-foreground">
                        {date}
                      </h3>
                    </div>
                    <div className="space-y-4 mt-4">
                      {activities.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-start gap-4 p-4 rounded-lg border hover:bg-accent/5 transition-colors"
                        >
                          <div className="mt-1">
                            {getActivityIcon(log.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              {getActivityMessage(log)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(
                                new Date(log.createdAt),
                                "dd MMM yyyy 'at' HH:mm",
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                currentPage={activityPage}
                totalPages={activity?.totalPages ?? 0}
                onPageChange={setActivityPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={USER_STATS_TAB.COURSES} className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Course Progress</CardTitle>
                  <p className="text-sm text-muted-foreground">All courses</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    placeholder="Search courses..."
                    value={searchInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchInput(e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        setCoursesPage(1);
                        setSearchTerm(searchInput);
                      }
                    }}
                    className="h-8 w-[200px]"
                  />
                  <button
                    onClick={() => {
                      setCoursesPage(1);
                      setSearchTerm(searchInput);
                    }}
                    className="h-8 px-3 rounded bg-primary text-white hover:bg-primary/90"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {coursesList.map((course) => {
                  const progress =
                    course.totalLevels > 0
                      ? (course.completedLevels / course.totalLevels) * 100
                      : 0;
                  const isExpanded = expandedCourseId === course.courseId;
                  const expandedDetail = personDetail?.courses.find(
                    (d) => d.courseId === course.courseId,
                  );

                  return (
                    <div key={course.courseId}>
                      <div
                        className="flex items-center gap-4 px-4 py-4 hover:bg-accent/5 transition-colors cursor-pointer"
                        onClick={() =>
                          setExpandedCourseId(
                            isExpanded ? null : course.courseId,
                          )
                        }
                      >
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <BookOpen className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium truncate">
                              {course.courseName}
                              {course.isDeleted && (
                                <span className="ml-2 text-xs text-muted-foreground font-normal">
                                  (Archived)
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-2">
                              <Trophy className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {course.totalPoints} pts
                              </span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                              <span>
                                {course.completedLevels} of {course.totalLevels}{' '}
                                levels
                              </span>
                              <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-accent/20">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <button className="flex items-center justify-center shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>

                      <div
                        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 py-3 bg-accent/5">
                            {personDetailLoading && !expandedDetail ? (
                              <div className="flex justify-center py-4">
                                <LoadingSpinner />
                              </div>
                            ) : expandedDetail ? (
                              <div className="space-y-1 pl-14">
                                {expandedDetail.levels.map((level) => (
                                  <div
                                    key={level.levelId}
                                    className="flex items-center gap-2 text-sm"
                                  >
                                    {level.isCompleted ? (
                                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <span
                                      className={
                                        level.isCompleted
                                          ? 'text-foreground'
                                          : 'text-muted-foreground'
                                      }
                                    >
                                      Level {level.levelNumber}:{' '}
                                      {level.levelName}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground pl-14">
                                No level data available
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination
                currentPage={coursesPage}
                totalPages={courses?.totalPages ?? 0}
                onPageChange={setCoursesPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={USER_STATS_TAB.TESTS} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tests</CardTitle>
              <p className="text-sm text-muted-foreground">All test sessions</p>
            </CardHeader>
            <CardContent>
              {/* Table Header */}
              <div className="grid grid-cols-[60px_1fr_110px_90px_70px] gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                <span>Test #</span>
                <span>Date</span>
                <span>Status</span>
                <span>Time Taken</span>
                <span className="text-right">Score</span>
              </div>

              <div className="divide-y">
                {userTests && userTests.tests.length > 0 ? (
                  userTests.tests.map((test, index) => {
                    const testNumber =
                      (userTests.total ?? 0) - ((testsPage - 1) * 10 + index);

                    return (
                      <div
                        key={test.id}
                        className="grid grid-cols-[60px_1fr_110px_90px_70px] gap-4 items-center px-4 py-3"
                      >
                        <span className="text-sm text-muted-foreground">
                          {testNumber}
                        </span>
                        <span className="text-sm">
                          {formatTestDate(test.endedAt, test.isGenerated)}
                        </span>
                        <TestStatusBadge status={test.status} />
                        <span className="text-sm">
                          {formatTestDuration(test.startedAt, test.endedAt)}
                        </span>
                        <span className="text-sm text-right font-medium">
                          {test.score}%
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No tests completed yet
                  </p>
                )}
              </div>
              <Pagination
                currentPage={testsPage}
                totalPages={userTests?.totalPages ?? 0}
                onPageChange={setTestsPage}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
